<script setup lang="ts">
/**
 * The draggable edge of a resizable panel, as in Financer's admin panel: drag it, move it with the
 * arrow keys once focused, or double click it to put the panel back to its initial width. `grows` is
 * the way the edge moves to widen the panel: `right` for a panel on the left, `left` for one on the
 * right. Placing it is the parent's job.
 */
const props = defineProps<{ grows: 'left' | 'right', label: string, max: number, min: number }>()
const emit = defineEmits<{ reset: [] }>()
const width = defineModel<number>({ required: true })

const KEY_STEP = 16
const sign = computed(() => (props.grows === 'right' ? 1 : -1))

function drag(event: PointerEvent) {
  const handle = event.currentTarget as HTMLElement
  const startX = event.clientX
  const startWidth = width.value
  const move = (moved: PointerEvent) => {
    width.value = startWidth + sign.value * (moved.clientX - startX)
  }
  // Captured, so the drag keeps going over iframes and canvases (the email and PDF editors).
  handle.setPointerCapture(event.pointerId)
  handle.addEventListener('pointermove', move)
  handle.addEventListener('lostpointercapture', () => handle.removeEventListener('pointermove', move), { once: true })
}

/** The arrow moves the edge; whether that widens the panel depends on the side it is on. */
function step(direction: -1 | 1) {
  width.value += direction * sign.value * KEY_STEP
}
</script>

<template>
  <!-- Wider to grab than the line it shows, with a grip in its middle. -->
  <div
    role="separator"
    aria-orientation="vertical"
    tabindex="0"
    :aria-label="label"
    :aria-valuenow="width"
    :aria-valuemin="min"
    :aria-valuemax="max"
    class="group outline-none flex shrink-0 w-2 cursor-col-resize items-center justify-center touch-none"
    @pointerdown.prevent="drag"
    @dblclick="emit('reset')"
    @keydown.left.prevent="step(-1)"
    @keydown.right.prevent="step(1)"
  >
    <div class="bg-border h-full w-px transition-colors group-focus-visible:bg-primary group-hover:bg-primary" />
    <div class="border rounded-full bg-muted h-7 w-2 shadow-xs transition-colors absolute group-focus-visible:border-primary group-hover:border-primary" />
  </div>
</template>
